import React, { useRef, useEffect, useState, useCallback } from "react";
import {
  Send,
  Upload,
  ChevronDown,
  ChevronUp,
  MessageSquare,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import moment from "moment";

const ChatMessagesPanel = ({
  messages,
  input,
  setInput,
  send,
  uploadedFile,
  loadingConversation = false,
  isSending = false,
  reportQuestions = [],
  sendMessage,
  showSuggestions = true,
}) => {
  const containerRef = useRef(null);
 const messagesEndRef = useRef(null)

  const [debugVisible, setDebugVisible] = useState({});
const [autoScroll, setAutoScroll] = useState(true);

  const toggleDebug = (i) => {
    setDebugVisible((prev) => ({
      ...prev,
      [i]: !prev[i],
    }));
  };
  function cleanMarkdown(text) {
    if (!text) return "";

    return text
      .replace(/\r?\n[ \t]*\r?\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .replace(/[ ]{2,}/g, " ")
      .replace(/^\s+|\s+$/g, "")
      .replace(/\n\s+\*/g, "\n*")
      .replace(/\n\s+\-/g, "\n-")
      .replace(/\n\s+#/g, "\n#");
  }

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);
  function downloadCsvBase64(base64, filename = "data.csv") {
    try {
      const cleaned = base64.replace(/\r?\n|\r/g, "");

      const csvText = atob(cleaned);

      const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("CSV download error:", e);
    }
  }

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    setAutoScroll(isAtBottom);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (el) {
      el.addEventListener("scroll", handleScroll, { passive: true });
      return () => el.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);
  useEffect(() => {
    if (autoScroll && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, autoScroll]);

  const fileName =
    uploadedFile?.name ??
    sessionStorage.getItem("selectedReportName") ??
    "No file selected";

  return (
    <div className="chat-wrapper">
      {(fileName || uploadedFile.name) && (
        <div className="file-info-bar">
          File: <strong>{fileName}</strong>
        </div>
      )}

      <div className="sb-body" ref={containerRef}>
        <div className="chat-center">
          {messages.length > 0 && (
            <div className="sb-date">{moment().format("MMMM DD, YYYY")}</div>
          )}

          {messages.length === 0 ? (
            loadingConversation ? (
              <div className="empty-screen">
                <div className="typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <div className="empty-subtitle">Loading conversation...</div>
              </div>
            ) : uploadedFile ? (
              <div className="empty-screen">
                <MessageSquare size={64} className="empty-icon" />
                <div className="empty-title">No messages yet</div>
                <div className="empty-subtitle">
                  Start chatting about your data!
                </div>
              </div>
            ) : (
              <div className="empty-screen">
                <Upload size={64} className="empty-icon" />
                <div className="empty-title">
                  Upload CSV File to Start Chatting
                </div>
                <div className="empty-subtitle">
                  Please upload a CSV file from the right panel to begin
                  chatting.
                </div>
              </div>
            )
          ) : (
            <>
              {messages.map((m, i) => {
                if (m.author === "user") {
                  return (
                    <div key={i} className="sb-msg user">
                      <div className="bubble">{m.text}</div>
                      <span>{moment(m.time).format("HH:mm")}</span>
                    </div>
                  );
                }

                if (m.type === "loader") {
                  return (
                    <div key={i} className="sb-msg bot">
                      <div className="bubble simple-processing">
                        <span className="processing-text">Processing…</span>
                        <div className="simple-dots">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      </div>
                    </div>
                  );
                }

                if (m.author === "assistant" && m.type === "answer") {
                  const hasAnswerText = m.text && m.text.trim() !== "";
                  const hasDebugText = m.debug && m.debug.trim() !== "";

                  return (
                    <div key={i} className="sb-msg bot">
                      <div className="bubble">
                        {m.isComplete && hasDebugText && hasAnswerText && (
                          <div
                            className="debug-toggle"
                            onClick={() => toggleDebug(i)}
                          >
                            {debugVisible[i] ? (
                              <>
                                <span>Hide Planning</span>
                                <ChevronUp size={20} />
                              </>
                            ) : (
                              <>
                                <span>Process Planning</span>
                                <ChevronDown size={20} />
                              </>
                            )}
                          </div>
                        )}

                        {hasDebugText &&
                          hasAnswerText &&
                          (debugVisible[i] || !m.isComplete) && (
                            <pre className="debug-block">
                              {m.debug}
                              {!m.isComplete && (
                                <span className="streaming-cursor">|</span>
                              )}
                            </pre>
                          )}

                        <div className="chat-pre markdown-body">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {cleanMarkdown(hasAnswerText ? m.text : m.debug)}
                          </ReactMarkdown>

                          {!m.isComplete && (
                            <span className="streaming-cursor">|</span>
                          )}
                        </div>

                        {m.charts?.map((c, idx) => (
                          <div key={idx} className="chart-wrapper">
                            {!c.isFile && (c.image || c.data) && (
                              <>
                                {c.title && (
                                  <div className="chart-title">{c.title}</div>
                                )}
                                <img
                                  src={
                                    (c.image || c.data).startsWith("data:")
                                      ? c.image || c.data
                                      : `data:image/png;base64,${c.image || c.data}`
                                  }
                                  alt="Chart"
                                  className="chart-img"
                                />
                              </>
                            )}

                            {c.isFile && (
                              <div className="download-card">
                                <div className="file-title">{c.filename}</div>
                                <button
                                  className="download-btn"
                                  onClick={() => {
                                    const fileName = c.filename
                                      .replace("/mnt/data/", "")
                                      .trim();
                                    downloadCsvBase64(c.data, fileName);
                                  }}
                                >
                                  Download CSV
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      <span>{moment(m.time).format("HH:mm")}</span>
                    </div>
                  );
                }

                if (m.type === "upload-success") {
                  return (
                    <div key={i} className="upload-success-card-wrap">
                      <div className="upload-success-card">
                        <span className="success-icon">✓</span>
                        <span className="success-text">{m.text}</span>
                      </div>
                    </div>
                  );
                }

                if (m.type === "upload-preview") {
                  return (
                    <div key={i} className="preview-table">
                      <div className="header">Data Preview (Sample Rows)</div>
                      <div className="preview-scroll">
                        <table>
                          <thead>
                            <tr>
                              {m.columns.slice(0, 8).map((col) => (
                                <th key={col}>{col}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {m.preview.map((row, idx) => (
                              <tr key={idx}>
                                {m.columns.slice(0, 8).map((col) => (
                                  <td key={col}>{row[col] ?? "-"}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                }

                return null;
              })}

              <div ref={messagesEndRef} />
            </>
          )}
        </div>
      </div>

      {showSuggestions && reportQuestions?.length > 0 && uploadedFile && (
        <div
          style={{
            padding: "10px",
            display: "flex",
            gap: "8px",
            overflowX: "auto",
            whiteSpace: "nowrap",
            borderTop: "1px solid #e2e8f0",
            background: "#f9fafb",
          }}
        >
          {reportQuestions.map((q, index) => (
            <button
              key={index}
              onClick={() => {
                setAutoScroll(true);
                sendMessage(q);
                setTimeout(() => {
                  scrollToBottom();
                }, 100);
              }}
              disabled={isSending}
              style={{
                flex: "0 0 auto",
                padding: "6px 14px",
                borderRadius: "20px",
                border: "1px solid #9333ea",
                background: "#fff",
                fontSize: "12px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              {q}
            </button>
          ))}
        </div>
      )}

      <div className="sb-input">
        <div className="input-center">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={
              uploadedFile
                ? "Ask me anything about your data..."
                : "Upload CSV file to start chatting"
            }
            disabled={!uploadedFile || isSending}
            onKeyDown={(e) => {
              if (e.key === "Enter" && uploadedFile && !isSending) {
                e.preventDefault();
                send();
              }
            }}
          />
          <button
            onClick={send}
            disabled={!input.trim() || !uploadedFile || isSending}
          >
            <Send />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatMessagesPanel;
